'use client';

import Image from 'next/image';
import { cn } from '@sharpr/ui';
import { AnimatedSection } from '@/components/client/landing/AnimatedSection';
import { DiscordNewsFeed } from '@/components/client/landing/DiscordNewsFeed';
import { GLASS_CARD, SectionEyebrow, SectionTitle } from './shared';

function ResearchImageFrame({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn(GLASS_CARD, className)}>{children}</div>;
}

export function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className={cn(GLASS_CARD, 'rounded-[2rem] p-2')}>
      <div className="rounded-[1.5rem] overflow-hidden">{children}</div>
    </div>
  );
}

export function NBAResearchRow() {
  return (
    <AnimatedSection id="nba-research" className="py-10 md:py-20 scroll-mt-16 md:scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-[3fr_2fr] gap-10 md:gap-14 items-center">
          <div className="hidden md:block">
            <ResearchImageFrame>
              <Image
                src="/nba_research-v4.webp"
                alt="Sharpr NBA player research showing game log bars, quarter breakdowns, shot chart, and recent form"
                width={2850}
                height={1140}
                sizes="60vw"
                className="w-full h-auto"
              />
            </ResearchImageFrame>
          </div>

          <div className="space-y-4">
            <SectionEyebrow>Research</SectionEyebrow>
            <SectionTitle>
              Every angle on every <span className="text-brand-500">player</span>
            </SectionTitle>
            <p className="text-base font-medium text-fg/70 leading-relaxed">
              Over/under splits, per-game trends, quarter breakdowns, shot
              zones, consistency signals, and recent form{' '}— all in one
              view. The context you need to size a bet with confidence.
            </p>
          </div>

          {/* Mobile: phone mock after the title + copy. */}
          <div className="md:hidden mx-auto max-w-[280px] w-full">
            <PhoneFrame>
              <Image
                src="/nba_research_mobile-v4.webp"
                alt="Sharpr mobile NBA research view showing game log bars and teammate assist graph"
                width={754}
                height={1586}
                sizes="280px"
                className="w-full h-auto"
              />
            </PhoneFrame>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
}

export function PitcherResearchRow() {
  return (
    <AnimatedSection id="pitcher-research" className="py-10 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-[2fr_3fr] gap-10 md:gap-14 items-center">
          <div className="space-y-4">
            <SectionEyebrow>News</SectionEyebrow>
            <SectionTitle>
              Be <span className="text-brand-500">first</span> to the news
            </SectionTitle>
            <p className="text-base font-medium text-fg/70 leading-relaxed">
              NBA projections with key players in or out. MLB projected
              lineups, confirmed first. All of it pushed to your Discord the
              second it breaks.
            </p>
          </div>

          {/* Animated Discord alert feed — reads fine at phone width, so it renders on mobile too. */}
          <DiscordNewsFeed />
        </div>
      </div>
    </AnimatedSection>
  );
}

export function MobileResearchRow() {
  return (
    <AnimatedSection id="mobile-research" className="hidden md:block py-10 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-[3fr_2fr] gap-10 md:gap-14 items-center">
          <div className="flex justify-center items-center gap-6 md:gap-8">
            <div className="w-full max-w-[260px] md:-translate-y-6">
              <PhoneFrame>
                <Image
                  src="/nba_research_mobile-v4.webp"
                  alt="Sharpr mobile research view showing a player's game log and teammate assist graph"
                  width={754}
                  height={1586}
                  sizes="260px"
                  className="w-full h-auto"
                />
              </PhoneFrame>
            </div>
            <div className="w-full max-w-[260px] md:translate-y-6">
              <PhoneFrame>
                <Image
                  src="/mlb_odds_mobile-v5.webp"
                  alt="Sharpr mobile MLB odds list showing player props with live EV badges"
                  width={758}
                  height={1620}
                  sizes="260px"
                  className="w-full h-auto"
                />
              </PhoneFrame>
            </div>
          </div>

          <div className="space-y-4">
            <SectionEyebrow>Mobile</SectionEyebrow>
            <SectionTitle>
              Research <span className="text-brand-500">anywhere</span>
            </SectionTitle>
            <p className="text-base font-medium text-fg/70 leading-relaxed">
              Every chart, every split, every edge{' '}— on a phone. Same
              data, built for your thumb.
            </p>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
}
